<!--Based on: https://github.com/vue-a11y/vue-focus-loop/blob/master/src/FocusLoop.vue-->
<template>
  <div :id="focusLoopContainerId">
    <div :tabindex="getTabindex" aria-hidden="true" @focus="handleFocusStart" />
    <div :id="focusLoopBodyId">
      <slot />
    </div>
    <div :tabindex="getTabindex" aria-hidden="true" @focus="handleFocusEnd" />
  </div>
</template>

<script>
import { getUniqueId } from '@/shared/utils/uniqueId.js'

const focusableElementsSelector = [
  ...['input', 'select', 'button', 'textarea'].map(
    (field) => `${field}:not([disabled]):not([tabindex^="-"])`,
  ),
  'a[href]',
  'video[controls]',
  'audio[controls]',
  '[tabindex]:not([tabindex^="-"])',
  '[contenteditable]:not([contenteditable="false"])',
].join(',')
let ariaHiddenElements = []

export default {
  name: 'FocusLoop',
  props: {
    isActive: {
      type: Boolean,
      default: false,
    },
    autoFocus: {
      type: Boolean,
      default: true,
    },
    isArrowsNavigationActive: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      alreadyFocused: false,
      focusLoopContainerId: `growthmentor-focusloop-container-${getUniqueId()}`,
      focusLoopBodyId: `growthmentor-focusloop-body-${getUniqueId()}`,
    }
  },
  computed: {
    getTabindex() {
      return this.isActive ? 0 : -1
    },
  },
  watch: {
    isActive: 'init',
  },
  mounted() {
    this.init()
  },
  methods: {
    init() {
      this.$nextTick(() => {
        if (this.isActive) {
          this.focusFirst(this.isActive && this.autoFocus)
          document.addEventListener('keydown', this.handleKeyupEventListener)
        } else {
          ariaHiddenElements = []
          document.removeEventListener('keydown', this.handleKeyupEventListener)
        }

        this.managePrevFocusElement(this.isActive)
        this.lockForSwipeScreenReader(this.isActive)
      })
    },
    managePrevFocusElement(active) {
      if (!active && window.vflPrevFocusedElement) {
        // If focusable elements don't include active element it means that someone uses mouse, and we don't want to change focus again.
        const focusableElements = [...this.getFocusableElements()]
        if (!focusableElements.includes(document.activeElement)) return

        return window.vflPrevFocusedElement.focus()
      }

      window.vflPrevFocusedElement = document.activeElement
    },
    getElementsToAriaHidden(focusLoopContainer) {
      function getElements(element) {
        const children = Array.from(element.children)
        children.forEach((el) => {
          if (el === focusLoopContainer) return
          if (!el.contains(focusLoopContainer)) {
            ariaHiddenElements.push(el)
            return
          }
          getElements(el)
        })
      }
      getElements(document.body)
    },
    lockForSwipeScreenReader(active = true) {
      if (active)
        this.getElementsToAriaHidden(
          document.getElementById(this.focusLoopContainerId),
        )
      ariaHiddenElements.forEach((el) => {
        if (
          ['SCRIPT', 'STYLE'].includes(el.nodeName) ||
          el.hasAttribute('aria-live')
        )
          return
        el.setAttribute('aria-hidden', active.toString())
      })
    },
    focusFirst(isAutoFocus) {
      if (!isAutoFocus) return

      const elements = this.getFocusableElements()
      if (elements.length) setTimeout(() => elements[0].focus(), 200)
    },
    handleFocusStart() {
      const elements = this.getFocusableElements()
      if (!elements.length) return

      const index = this.alreadyFocused ? elements.length - 1 : 0
      this.alreadyFocused = true
      elements[index].focus()
    },
    handleFocusEnd() {
      const elements = this.getFocusableElements()
      elements.length && elements[0].focus()
    },
    getFocusableElements() {
      const focusLoopBodyElement = document.getElementById(this.focusLoopBodyId)
      if (!focusLoopBodyElement) return []
      const focusableElements = focusLoopBodyElement.querySelectorAll(
        focusableElementsSelector,
      )
      if (focusableElements && focusableElements.length)
        return focusableElements
      return []
    },
    handleKeyupEventListener(event) {
      const elements = Array.from(this.getFocusableElements())

      const keysToListen = this.isArrowsNavigationActive
        ? ['ArrowDown', 'ArrowUp', 'Tab']
        : ['Tab']

      if (!keysToListen.includes(event.key)) return
      event.preventDefault()
      event.stopPropagation()

      const activeIndex = elements.indexOf(document.activeElement)

      if (event.key === 'ArrowUp' || (event.shiftKey && event.key === 'Tab')) {
        const previousElement = elements[activeIndex - 1]
        if (previousElement) previousElement.focus()
        else elements[elements.length - 1].focus()
      }

      if (
        event.key === 'ArrowDown' ||
        (!event.shiftKey && event.key === 'Tab')
      ) {
        const nextElement = elements[activeIndex + 1]
        if (nextElement) nextElement.focus()
        else elements[0].focus()
      }
    },
  },
}
</script>
